import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { FormattedMessage } from 'react-intl-next';
import { withAnalyticsContext } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/standard-button';
import { IntlMessagesProvider } from '@atlaskit/intl-messages-provider';
import { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import { EVENT_CHANNEL, useDatasourceAnalyticsEvents } from '../../../analytics';
import { componentMetadata } from '../../../analytics/constants';
import { DatasourceAction, DatasourceDisplay, DatasourceSearchMethod } from '../../../analytics/types';
import { startUfoExperience } from '../../../analytics/ufoExperiences';
import { useColumnPickerRenderedFailedUfoExperience } from '../../../analytics/ufoExperiences/hooks/useColumnPickerRenderedFailedUfoExperience';
import { useDataRenderedUfoExperience } from '../../../analytics/ufoExperiences/hooks/useDataRenderedUfoExperience';
import { buildDatasourceAdf } from '../../../common/utils/adf';
import { fetchMessagesForLocale } from '../../../common/utils/locale/fetch-messages-for-locale';
import { DatasourceExperienceIdProvider, useDatasourceExperienceId } from '../../../contexts/datasource-experience-id';
import { UserInteractionsProvider, useUserInteractions } from '../../../contexts/user-interactions';
import { useAssetsClient } from '../../../hooks/useAssetsClient';
import { useDatasourceTableState } from '../../../hooks/useDatasourceTableState';
import i18nEN from '../../../i18n/en';
import { PermissionError } from '../../../services/cmdbService.utils';
import { StoreContainer } from '../../../state';
import { AccessRequired } from '../../../ui/common/error-state/access-required';
import { ModalLoadingError } from '../../common/error-state/modal-loading-error';
import { CancelButton } from '../../common/modal/cancel-button';
import { DatasourceModal } from '../../common/modal/datasource-modal';
import { AssetsSearchContainer } from '../search-container';
import { AssetsSearchContainerLoading } from '../search-container/loading-state';
import { modalMessages } from './messages';
import { MODAL_HEIGHT, RenderAssetsContent } from './render-assets-content';
var VERSION_TWO = '2';
var modalBodyErrorWrapperStyles = css({
  alignItems: 'center',
  display: 'grid',
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
  height: MODAL_HEIGHT
});
var AssetsModalTitle = jsx(ModalTitle, null, jsx(FormattedMessage, modalMessages.insertObjectsTitle));
var PlainAssetsConfigModal = function PlainAssetsConfigModal(props) {
  var datasourceId = props.datasourceId,
    initialParameters = props.parameters,
    onCancel = props.onCancel,
    onInsert = props.onInsert,
    initialVisibleColumnKeys = props.visibleColumnKeys;
  var _useState = useState(initialParameters === null || initialParameters === void 0 ? void 0 : initialParameters.aql),
    _useState2 = _slicedToArray(_useState, 2),
    aql = _useState2[0],
    setAql = _useState2[1];
  var _useState3 = useState(initialParameters === null || initialParameters === void 0 ? void 0 : initialParameters.schemaId),
    _useState4 = _slicedToArray(_useState3, 2),
    schemaId = _useState4[0],
    setSchemaId = _useState4[1];
  var apiVersion = initialParameters === null || initialParameters === void 0 ? void 0 : initialParameters.version;
  var _useState5 = useState(apiVersion !== VERSION_TWO ? [] : initialVisibleColumnKeys),
    _useState6 = _slicedToArray(_useState5, 2),
    visibleColumnKeys = _useState6[0],
    setVisibleColumnKeys = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    isNewSearch = _useState8[0],
    setIsNewSearch = _useState8[1];
  var _useState9 = useState(),
    _useState10 = _slicedToArray(_useState9, 2),
    errorState = _useState10[0],
    setErrorState = _useState10[1];
  var _useDatasourceAnalyti = useDatasourceAnalyticsEvents(),
    fireEvent = _useDatasourceAnalyti.fireEvent;
  var experienceId = useDatasourceExperienceId();
  var _useAssetsClient = useAssetsClient(initialParameters),
    workspaceId = _useAssetsClient.workspaceId,
    workspaceError = _useAssetsClient.workspaceError,
    existingObjectSchema = _useAssetsClient.existingObjectSchema,
    existingObjectSchemaError = _useAssetsClient.existingObjectSchemaError,
    objectSchemas = _useAssetsClient.objectSchemas,
    objectSchemasError = _useAssetsClient.objectSchemasError,
    totalObjectSchemas = _useAssetsClient.totalObjectSchemas,
    assetsClientLoading = _useAssetsClient.assetsClientLoading;

  /* ------------------------------ PERMISSIONS ------------------------------ */
  useEffect(function () {
    if (workspaceError) {
      // If a workspaceError occurs this is a critical error
      if (workspaceError instanceof PermissionError) {
        setErrorState('permission');
      } else {
        setErrorState('network');
      }
    }
  }, [workspaceError]);
  useEffect(function () {
    if (objectSchemasError) {
      // We only care about permission errors for objectSchemas fetching as the user can retry this action
      if (objectSchemasError instanceof PermissionError) {
        setErrorState('permission');
      }
    }
  }, [objectSchemasError]);
  useEffect(function () {
    if (existingObjectSchemaError) {
      // We only care about permission errors for existingObjectSchema fetching as the user can retry this action
      if (existingObjectSchemaError instanceof PermissionError) {
        setErrorState('permission');
      }
    }
  }, [existingObjectSchemaError]);
  /* ------------------------------ END PERMISSIONS ------------------------------ */

  var parameters = useMemo(function () {
    return {
      aql: aql || '',
      schemaId: schemaId || '',
      workspaceId: workspaceId || '',
      version: VERSION_TWO
    };
  }, [aql, schemaId, workspaceId]);
  var isParametersSet = !!(aql && workspaceId && schemaId);
  var _useDatasourceTableSt = useDatasourceTableState({
      datasourceId: datasourceId,
      parameters: isParametersSet ? parameters : undefined,
      fieldKeys: isNewSearch ? [] : visibleColumnKeys
    }),
    status = _useDatasourceTableSt.status,
    onNextPage = _useDatasourceTableSt.onNextPage,
    responseItems = _useDatasourceTableSt.responseItems,
    responseItemIds = _useDatasourceTableSt.responseItemIds,
    reset = _useDatasourceTableSt.reset,
    loadDatasourceDetails = _useDatasourceTableSt.loadDatasourceDetails,
    hasNextPage = _useDatasourceTableSt.hasNextPage,
    columns = _useDatasourceTableSt.columns,
    defaultVisibleColumnKeys = _useDatasourceTableSt.defaultVisibleColumnKeys,
    _useDatasourceTableSt2 = _useDatasourceTableSt.extensionKey,
    extensionKey = _useDatasourceTableSt2 === void 0 ? null : _useDatasourceTableSt2,
    destinationObjectTypes = _useDatasourceTableSt.destinationObjectTypes,
    totalCount = _useDatasourceTableSt.totalCount;

  /* ------------------------------ OBSERVABILITY ------------------------------ */
  var searchCount = useRef(0);
  var userInteractions = useUserInteractions();
  var visibleColumnCount = useRef((visibleColumnKeys === null || visibleColumnKeys === void 0 ? void 0 : visibleColumnKeys.length) || 0);
  var isDataReady = (visibleColumnKeys || []).length > 0;
  var analyticsPayload = useMemo(function () {
    return {
      extensionKey: extensionKey,
      destinationObjectTypes: destinationObjectTypes
    };
  }, [destinationObjectTypes, extensionKey]);
  useEffect(function () {
    // We only want to send modal ready event once after we've fetched the schema count
    if (totalObjectSchemas !== undefined) {
      fireEvent('ui.modal.ready.datasource', {
        schemasCount: totalObjectSchemas,
        instancesCount: null
      });
    }
  }, [fireEvent, totalObjectSchemas]);
  var fireTableViewedEvent = useCallback(function () {
    if (isDataReady) {
      fireEvent('ui.table.viewed.datasourceConfigModal', _objectSpread(_objectSpread({}, analyticsPayload), {}, {
        totalItemCount: totalCount || 0,
        searchMethod: DatasourceSearchMethod.DATASOURCE_SEARCH_QUERY,
        displayedColumnCount: visibleColumnCount.current
      }));
    }
  }, [analyticsPayload, fireEvent, totalCount, isDataReady]);
  useEffect(function () {
    var isResolved = status === 'resolved';
    if (!isResolved || !totalCount) {
      return;
    }
    if (totalCount > 1) {
      fireTableViewedEvent();
    }
  }, [fireTableViewedEvent, status, totalCount]);
  useEffect(function () {
    var shouldStartUfoExperience = status === 'loading';
    if (shouldStartUfoExperience) {
      startUfoExperience({
        name: 'datasource-rendered'
      }, experienceId);
    }
  }, [experienceId, status]);
  useDataRenderedUfoExperience({
    status: status,
    experienceId: experienceId,
    itemCount: responseItems.length,
    canBeLink: false,
    extensionKey: extensionKey
  });
  useColumnPickerRenderedFailedUfoExperience(status, experienceId);
  /* ------------------------------ END OBSERVABILITY ------------------------------ */

  var onVisibleColumnKeysChange = useCallback(function (visibleColumnKeys) {
    setVisibleColumnKeys(visibleColumnKeys);
    setIsNewSearch(false);
  }, []);
  useEffect(function () {
    var newVisibleColumnKeys = initialVisibleColumnKeys && initialVisibleColumnKeys.length > 0 && apiVersion === VERSION_TWO ? initialVisibleColumnKeys : defaultVisibleColumnKeys;
    setVisibleColumnKeys(newVisibleColumnKeys);
  }, [initialVisibleColumnKeys, defaultVisibleColumnKeys, apiVersion]);
  useEffect(function () {
    if (isNewSearch) {
      setVisibleColumnKeys(defaultVisibleColumnKeys);
    }
  }, [defaultVisibleColumnKeys, isNewSearch]);
  useEffect(function () {
    visibleColumnCount.current = (visibleColumnKeys !== null && visibleColumnKeys !== void 0 ? visibleColumnKeys : []).length;
  }, [visibleColumnKeys]);
  var isDisabled = !!errorState || status !== 'resolved' || assetsClientLoading || !aql || !schemaId;
  var isEditingExistingTable = !!(initialParameters !== null && initialParameters !== void 0 && initialParameters.aql && initialParameters !== null && initialParameters !== void 0 && initialParameters.schemaId && initialParameters !== null && initialParameters !== void 0 && initialParameters.workspaceId);
  var retrieveUrlForSmartCardRender = useCallback(function () {
    var _data$key;
    var _responseItems = _slicedToArray(responseItems, 1),
      data = _responseItems[0];
    // agreement with BE that we will use `key` for rendering smartlink
    return data === null || data === void 0 || (_data$key = data.key) === null || _data$key === void 0 || (_data$key = _data$key.data) === null || _data$key === void 0 ? void 0 : _data$key.url;
  }, [responseItems]);
  var onInsertPressed = useCallback(function (e, analyticsEvent) {
    var _insertButtonClickedE;
    if (!aql || !schemaId || !workspaceId) {
      return;
    }
    var insertButtonClickedEvent = analyticsEvent.update({
      actionSubjectId: 'insert',
      attributes: _objectSpread(_objectSpread({}, analyticsPayload), {}, {
        totalItemCount: totalCount || 0,
        displayedColumnCount: visibleColumnCount.current,
        display: DatasourceDisplay.DATASOURCE_TABLE,
        searchCount: searchCount.current,
        searchMethod: DatasourceSearchMethod.DATASOURCE_SEARCH_QUERY,
        actions: userInteractions.get()
      }),
      eventType: 'ui'
    });
    var consumerEvent = (_insertButtonClickedE = insertButtonClickedEvent.clone()) !== null && _insertButtonClickedE !== void 0 ? _insertButtonClickedE : undefined;
    insertButtonClickedEvent.fire(EVENT_CHANNEL);
    var firstAssetUrl = retrieveUrlForSmartCardRender();
    if (responseItems.length === 1 && firstAssetUrl) {
      onInsert({
        type: 'inlineCard',
        attrs: {
          url: firstAssetUrl
        }
      }, consumerEvent);
    } else {
      onInsert(buildDatasourceAdf({
        id: datasourceId,
        parameters: {
          workspaceId: workspaceId,
          aql: aql,
          schemaId: schemaId,
          version: VERSION_TWO
        },
        views: [{
          type: 'table',
          properties: {
            columns: (visibleColumnKeys !== null && visibleColumnKeys !== void 0 ? visibleColumnKeys : []).map(function (key) {
              return {
                key: key
              };
            })
          }
        }]
      }), consumerEvent);
    }
  }, [aql, schemaId, workspaceId, analyticsPayload, totalCount, userInteractions, retrieveUrlForSmartCardRender, responseItems.length, onInsert, datasourceId, visibleColumnKeys]);
  var handleOnSearch = useCallback(function (searchAql, searchSchemaId) {
    if (schemaId !== searchSchemaId || aql !== searchAql || status === 'rejected') {
      searchCount.current++;
      if (schemaId !== searchSchemaId) {
        userInteractions.add(DatasourceAction.SCHEMA_UPDATED);
      }
      if (aql !== searchAql) {
        userInteractions.add(DatasourceAction.QUERY_UPDATED);
      }
      setAql(searchAql);
      setSchemaId(searchSchemaId);
      setVisibleColumnKeys([]);
      setIsNewSearch(true);
      reset({
        shouldForceRequest: true,
        shouldResetColumns: true
      });
    }
  }, [aql, reset, schemaId, status, userInteractions]);
  var renderErrorState = useCallback(function () {
    if (errorState) {
      switch (errorState) {
        case 'permission':
          return jsx(AccessRequired, null);
        case 'network':
          return jsx(ModalLoadingError, null);
        default:
          return jsx(ModalLoadingError, null);
      }
    }
  }, [errorState]);
  var renderModalTitleContent = useCallback(function () {
    if (errorState) {
      return undefined;
    } else {
      if (!workspaceId || assetsClientLoading) {
        return jsx(AssetsSearchContainerLoading, {
          modalTitle: AssetsModalTitle
        });
      }
      return jsx(AssetsSearchContainer, {
        workspaceId: workspaceId,
        initialSearchData: {
          aql: initialParameters === null || initialParameters === void 0 ? void 0 : initialParameters.aql,
          objectSchema: existingObjectSchema,
          objectSchemas: objectSchemas
        },
        onSearch: handleOnSearch,
        modalTitle: AssetsModalTitle,
        isSearching: status === 'loading'
      });
    }
  }, [errorState, workspaceId, assetsClientLoading, initialParameters === null || initialParameters === void 0 ? void 0 : initialParameters.aql, existingObjectSchema, objectSchemas, handleOnSearch, status]);
  var getCancelButtonAnalyticsPayload = useCallback(function () {
    return _objectSpread(_objectSpread({}, analyticsPayload), {}, {
      searchCount: searchCount.current,
      actions: userInteractions.get()
    });
  }, [analyticsPayload, userInteractions]);
  return jsx(IntlMessagesProvider, {
    defaultMessages: i18nEN,
    loaderFn: fetchMessagesForLocale
  }, jsx(ModalTransition, null, jsx(DatasourceModal, {
    testId: "asset-datasource-modal",
    onClose: onCancel
  }, jsx(ModalHeader, null, renderModalTitleContent()), jsx(ModalBody, null, errorState ? jsx("div", {
    css: modalBodyErrorWrapperStyles
  }, renderErrorState()) : jsx(RenderAssetsContent, {
    isFetchingInitialData: assetsClientLoading,
    status: status,
    responseItems: responseItems,
    responseItemIds: responseItemIds,
    visibleColumnKeys: visibleColumnKeys,
    onVisibleColumnKeysChange: onVisibleColumnKeysChange,
    datasourceId: datasourceId,
    aql: aql,
    schemaId: schemaId,
    onNextPage: onNextPage,
    hasNextPage: hasNextPage,
    loadDatasourceDetails: loadDatasourceDetails,
    columns: columns,
    defaultVisibleColumnKeys: defaultVisibleColumnKeys
  })), jsx(ModalFooter, null, jsx(CancelButton, {
    onCancel: onCancel,
    getAnalyticsPayload: getCancelButtonAnalyticsPayload,
    testId: 'asset-datasource-modal--cancel-button'
  }), jsx(Button, {
    appearance: "primary",
    onClick: onInsertPressed,
    isDisabled: isDisabled,
    testId: 'assets-datasource-modal--insert-button'
  }, jsx(FormattedMessage, _extends({}, isEditingExistingTable ? modalMessages.updateObjectsButtonText : modalMessages.insertIssuesButtonText, {
    values: {
      objectsCount: responseItems.length
    }
  })))))));
};
var analyticsContextAttributes = {
  dataProvider: 'jsm-assets'
};
var analyticsContextData = _objectSpread(_objectSpread({}, componentMetadata.configModal), {}, {
  source: 'datasourceConfigModal'
});
var contextData = _objectSpread(_objectSpread({}, analyticsContextData), {}, {
  attributes: _objectSpread({}, analyticsContextAttributes)
});
export var AssetsConfigModal = withAnalyticsContext(contextData)(function (props) {
  return jsx(StoreContainer, null, jsx(DatasourceExperienceIdProvider, null, jsx(UserInteractionsProvider, null, jsx(PlainAssetsConfigModal, props))));
});